import {
  AccountTree as JourneyIcon,
  Business as BusinessIcon,
  ChevronRight as ChevronRightIcon,
  CorporateFare as LegalIcon,
  Edit as EditIcon,
  Label as LabelIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import useGetCompanyMarkingNr from "../../../hooks/useGetCompanyMarkingNr";
import {
  Customer,
  useGetSalesEmployeesQuery,
} from "../../../store/services/customerService";
import AssignSalesDialog from "../../dialogs/AssignSales";
import UpdateCustomerSegmentDialog from "../../dialogs/UpdateCustomerSegmentDialog";

const INK = "#181614";
const INK_3 = "#6B655F";
const INK_4 = "#9B948D";
const LINE = "#ECE7E0";

interface QuickInfoCardProps {
  customer: Customer;
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  action?: React.ReactNode;
  onClick?: () => void;
}

const InfoRow = ({ icon, label, value, sub, action, onClick }: InfoRowProps) => (
  <Box
    onClick={onClick}
    sx={{
      display: "grid",
      gridTemplateColumns: "28px 1fr auto",
      alignItems: "center",
      gap: 1.5,
      p: "9px 4px",
      borderRadius: "8px",
      cursor: onClick ? "pointer" : "default",
      "&:not(:first-of-type)": {
        borderTop: `1px dashed ${LINE}`,
      },
      "&:hover": onClick ? { bgcolor: "rgba(0,0,0,.03)" } : {},
    }}
  >
    <Box
      sx={{
        color: INK_3,
        display: "grid",
        placeItems: "center",
        "& .MuiSvgIcon-root": { fontSize: 18 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 11,
          color: INK_3,
          textTransform: "uppercase",
          letterSpacing: ".06em",
          fontWeight: 600,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          color: INK,
          fontWeight: 500,
          lineHeight: 1.4,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value || "—"}
      </Typography>
      {sub && (
        <Typography
          sx={{
            fontSize: 12,
            color: INK_3,
            fontWeight: 400,
            lineHeight: 1.35,
          }}
        >
          {sub}
        </Typography>
      )}
    </Box>
    {action && <Box>{action}</Box>}
  </Box>
);

const QuickInfoCard = ({ customer }: QuickInfoCardProps) => {
  const navigate = useNavigate();
  const [salesOpen, setSalesOpen] = React.useState(false);
  const [segmentOpen, setSegmentOpen] = React.useState(false);
  const { data: employees = [] } = useGetSalesEmployeesQuery();

  const salesResponsible = employees.find(
    (e) => e.ExternalUserNo === customer.SalesResponsible?.ExternalUserNo
  );
  const salesAvatarSrc = salesResponsible?.ContactPersonData?.ImageData
    ? `data:image/png;base64,${salesResponsible.ContactPersonData.ImageData}`
    : undefined;
  const salesEmail = salesResponsible?.ContactPersonData?.Email;

  const markingsGroupNr = useGetCompanyMarkingNr();
  const customerMarkingGroup = customer.CustomerGroups?.find(
    (m) => m.Id === markingsGroupNr
  );
  const customerMarkingName = customerMarkingGroup?.Alternatives.find((x) => {
    const selectedValue = customerMarkingGroup?.SelectedValue;
    if (!selectedValue) return false;
    return x.Id === Number(selectedValue);
  })?.Name;

  const journey = customer.CustomerJourneyType?.find((j) => j.Selected);
  const segment = customer.CustomerSegments?.find((s) => s.Selected);
  const legalEntity = customer.LegalEntityType?.find((e) => e.Selected);

  const editIconBtn = (
    onClick: () => void,
    label = "Redigera"
  ): React.ReactNode => (
    <IconButton
      size="small"
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        color: INK_4,
        "&:hover": { color: INK, bgcolor: "rgba(0,0,0,.04)" },
        "& .MuiSvgIcon-root": { fontSize: 16 },
      }}
    >
      <EditIcon />
    </IconButton>
  );

  return (
    <Card
      sx={{
        borderRadius: "14px",
        border: `1px solid ${LINE}`,
        bgcolor: "background.paper",
        boxShadow:
          "0 1px 2px rgba(24,22,20,.04), 0 1px 1px rgba(24,22,20,.03)",
      }}
    >
      <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 } }}>
        <Stack
          direction="row"
          alignItems="center"
          gap={1.25}
          sx={{ mb: 1.25 }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: "6px",
              bgcolor: "rgba(255,95,0,.1)",
              color: "#FF5F00",
              display: "grid",
              placeItems: "center",
              "& .MuiSvgIcon-root": { fontSize: 14 },
            }}
          >
            <BusinessIcon />
          </Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".08em",
              color: INK,
            }}
          >
            Snabbinfo
          </Typography>
        </Stack>

        <Box>
          {customer.LegalEntityValue && (
            <InfoRow
              icon={<BusinessIcon />}
              label="Org.nr"
              value={customer.LegalEntityValue}
            />
          )}
          {customer.CustomerAddress && (
            <InfoRow
              icon={<LocationIcon />}
              label="Adress"
              value={customer.CustomerAddress}
            />
          )}
          {journey && (
            <InfoRow
              icon={<JourneyIcon />}
              label="Kundresa"
              value={journey.Name.trim()}
            />
          )}
          {legalEntity && (
            <InfoRow
              icon={<LegalIcon />}
              label="Bolagsform"
              value={legalEntity.LegalEntityTypeName}
              sub={segment?.Name}
              action={editIconBtn(() => setSegmentOpen(true), "Redigera segment")}
            />
          )}
          {customerMarkingName && (
            <InfoRow
              icon={<LabelIcon />}
              label="Markering"
              value={customerMarkingName}
              sub="Visa alla i gruppen"
              action={
                <ChevronRightIcon sx={{ fontSize: 16, color: INK_4 }} />
              }
              onClick={() =>
                navigate(`/customer/${customer.CustomerNo}/groups`)
              }
            />
          )}
          <InfoRow
            icon={
              salesAvatarSrc || salesResponsible?.Name ? (
                <Avatar
                  src={salesAvatarSrc}
                  sx={{
                    width: 22,
                    height: 22,
                    fontSize: 11,
                    bgcolor: "rgba(255,95,0,.1)",
                    color: "#FF5F00",
                  }}
                >
                  {salesResponsible?.Name?.[0] ?? "?"}
                </Avatar>
              ) : (
                <PersonIcon />
              )
            }
            label="Säljare"
            value={salesResponsible?.Name ?? "Ej tilldelad"}
            sub={salesEmail}
            action={editIconBtn(() => setSalesOpen(true), "Tilldela säljare")}
          />
        </Box>
      </CardContent>

      <AssignSalesDialog
        open={salesOpen}
        onClose={() => setSalesOpen(false)}
        customerNo={customer.CustomerNo}
      />
      <UpdateCustomerSegmentDialog
        open={segmentOpen}
        onClose={() => setSegmentOpen(false)}
        customerIds={[customer.CustomerNo]}
      />
    </Card>
  );
};

export default QuickInfoCard;
